import { BLACK, WHITE, type Color } from './color.js'

export const PAWN = 1
export const KNIGHT = 2
export const BISHOP = 3
export const ROOK = 4
export const QUEEN = 5
export const KING = 6

export type PieceType =
  | typeof PAWN
  | typeof KNIGHT
  | typeof BISHOP
  | typeof ROOK
  | typeof QUEEN
  | typeof KING

/** The only things a pawn may become. Excluding king and pawn at the type level. */
export type PromotionPiece = typeof KNIGHT | typeof BISHOP | typeof ROOK | typeof QUEEN

export const PROMOTION_PIECES = [QUEEN, ROOK, BISHOP, KNIGHT] as const

export type Piece = {
  readonly color: Color
  readonly type: PieceType
}

/**
 * Board squares hold a packed byte rather than an object: `type | color << 3`.
 * This keeps the board a `Uint8Array`, which sidesteps
 * `noUncheckedIndexedAccess` noise in the hot loops and makes cloning a
 * position a single memcpy.
 */
export type PieceCode = number

export const EMPTY: PieceCode = 0

export const encodePiece = (color: Color, type: PieceType): PieceCode => type | (color << 3)

export const codeType = (code: PieceCode): PieceType => (code & 7) as PieceType
export const codeColor = (code: PieceCode): Color => ((code >> 3) & 1) as Color
export const isEmpty = (code: PieceCode): boolean => code === EMPTY
export const isOccupiedBy = (code: PieceCode, color: Color): boolean =>
  code !== EMPTY && codeColor(code) === color

export const decodePiece = (code: PieceCode): Piece | null =>
  code === EMPTY ? null : { color: codeColor(code), type: codeType(code) }

const SYMBOLS = 'pnbrqk' as const

export type PieceSymbol = 'p' | 'n' | 'b' | 'r' | 'q' | 'k'
export type PieceLetter = PieceSymbol | 'P' | 'N' | 'B' | 'R' | 'Q' | 'K'

/** `P` for a white pawn, `p` for a black one — the FEN convention. */
export const pieceLetter = (code: PieceCode): PieceLetter => {
  const symbol = SYMBOLS[codeType(code) - 1] as PieceSymbol
  return (codeColor(code) === WHITE ? symbol.toUpperCase() : symbol) as PieceLetter
}

export const pieceFromLetter = (letter: string): PieceCode | null => {
  const lower = letter.toLowerCase()
  const index = SYMBOLS.indexOf(lower as PieceSymbol)
  if (index === -1) return null
  const type = (index + 1) as PieceType
  return encodePiece(letter === lower ? BLACK : WHITE, type)
}

/** Uppercase, as SAN writes it. Pawns have no letter in SAN. */
export const typeLetter = (type: PieceType): 'P' | 'N' | 'B' | 'R' | 'Q' | 'K' =>
  (SYMBOLS[type - 1] as PieceSymbol).toUpperCase() as 'P' | 'N' | 'B' | 'R' | 'Q' | 'K'

export const typeFromLetter = (letter: string): PieceType | null => {
  const index = SYMBOLS.indexOf(letter.toLowerCase() as PieceSymbol)
  return index === -1 ? null : ((index + 1) as PieceType)
}
